'use client';

import { useCallback, useRef } from 'react';
import { create } from 'zustand';
import { cn } from '@/shared/lib/cn';

/**
 * Where a screen wants the AI architect's button. The button belongs to the
 * assistant feature, but its place belongs to the screen: in the header
 * toolbar next to Feedback on a wide window, beside the floating Generate on a
 * phone's design screen. A screen marks the place with a slot; the assistant
 * renders its button there and falls back to the screen corner when no slot is
 * mounted. Screens stay free of any assistant import beyond this marker.
 */
export type AssistantLauncherPlacement = 'toolbar' | 'footer';

interface LauncherSlotsState {
  slots: Partial<Record<AssistantLauncherPlacement, HTMLElement>>;
  mount: (placement: AssistantLauncherPlacement, element: HTMLElement) => void;
  /** Only clears the placement while it still points at this element: the
   *  next screen's slot may already have mounted when the old one leaves. */
  unmount: (placement: AssistantLauncherPlacement, element: HTMLElement) => void;
}

export const useAssistantLauncherSlots = create<LauncherSlotsState>((set) => ({
  slots: {},
  mount: (placement, element) =>
    set((state) => ({ slots: { ...state.slots, [placement]: element } })),
  unmount: (placement, element) =>
    set((state) => {
      if (state.slots[placement] !== element) return state;
      const slots = { ...state.slots };
      delete slots[placement];
      return { slots };
    }),
}));

export function AssistantLauncherSlot({
  placement,
  tone,
  className,
}: {
  placement: AssistantLauncherPlacement;
  /** `hero`: the glass pill of the building page's header, like its Feedback. */
  tone?: 'hero';
  className?: string;
}) {
  const mounted = useRef<HTMLDivElement | null>(null);
  const register = useCallback(
    (element: HTMLDivElement | null) => {
      const slots = useAssistantLauncherSlots.getState();
      if (mounted.current) slots.unmount(placement, mounted.current);
      mounted.current = element;
      if (element) slots.mount(placement, element);
    },
    [placement]
  );
  // Empty (zero size) until the assistant renders its button into it.
  return <div ref={register} data-tone={tone} className={cn('flex items-center', className)} />;
}
